import type { Grant } from './grant';
import type { InviteAddressResult } from './invites';
import type { OpenInvite } from './studyPeople';

/** One member of a group, as `GET study/:id/group` lists them. Hex id. */
export interface NetworkGroupMember {
  identifier: string;
  name: string;
  avatar?: string;
  email: string;
}

/**
 * One group as `GET study/:id/group` sends it (users decision 74): its grant, its members oldest
 * first, and the open invites holding it. Matches spdata-api's `GroupRow` in `src/group/`.
 */
export interface NetworkGroupRow {
  id: string;
  name: string;
  grant: Grant;
  members: NetworkGroupMember[];
  pendingInvites: OpenInvite[];
}

export interface NetworkGroupList {
  groups: NetworkGroupRow[];
}

/** `POST study/:id/group` (users decision 64). `memberIds` are current members; `invite` holds addresses. */
export interface GroupCreateBody {
  name: string;
  grant: Grant;
  memberIds?: string[];
  invite?: string[];
}

/** `PATCH study/:id/group/:groupId`: only what changed, members as a diff (users decision 64). */
export interface GroupEditBody {
  name?: string;
  grant?: Grant;
  members?: { add: string[]; remove: string[] };
  invite?: string[];
}

/** One address a group save sent to the invite writer: the invite service's per-address result. */
export type GroupInviteResult = InviteAddressResult;

/** What a group save tells the dialog: each invite address's outcome. A current member's address has none. */
export interface GroupSaveResult {
  invites: GroupInviteResult[];
}

const addressOrder = new Intl.Collator('en');

/**
 * A group's open invites A to Z by address, case-folded, then by id: the order the People list gives
 * invites (users decision 111). The server sends them in its own order, which put an invite edited or
 * re-sent last (ENG-10282).
 */
export const invitesByAddress = (invites: OpenInvite[]): OpenInvite[] =>
  [...invites].sort((a, b) =>
    addressOrder.compare(a.address.toLowerCase(), b.address.toLowerCase()) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

/** The addresses a save staged for an invite that got no email: refused as invalid, or invited with the email failed. */
export const unsentInvites = (result: GroupSaveResult): string[] =>
  result.invites
    .filter(invite => invite.result === 'invalid' || (invite.result === 'invited' && !invite.emailSent))
    .map(invite => invite.address);

/** What `DELETE study/:id/group/:groupId` answers (users decision 72). */
export interface GroupDeleteResult {
  membersLeft: number;
  invitesCancelled: number;
}

export class StudyGroupModel {
  identifier: string;
  studyIdentifier: string;
  name: string;
  grant: Grant;
  members: NetworkGroupMember[];
  pendingInvites: OpenInvite[];

  constructor({
    identifier,
    studyIdentifier,
    name,
    grant,
    members,
    pendingInvites
  }: StudyGroupModel) {
    this.identifier = identifier;
    this.studyIdentifier = studyIdentifier;
    this.name = name;
    this.grant = grant;
    this.members = members;
    this.pendingInvites = pendingInvites;
  }

  /** A group from `GET study/:id/group`, as the server sent it: members and open invites apart (d20). */
  static fromGroupRow(row: NetworkGroupRow, studyIdentifier: string) {
    return new StudyGroupModel({
      identifier: row.id,
      studyIdentifier,
      name: row.name,
      grant: row.grant,
      members: row.members,
      pendingInvites: row.pendingInvites
    });
  }
}

/**
 * The open invites a delete of this group cancels: those it is the only live group on, since the read
 * lists live groups alone (users decision 72). The rest only lose this group.
 */
export const invitesCancelledByDelete = (group: Pick<StudyGroupModel, 'pendingInvites'>): number =>
  group.pendingInvites.filter(invite => invite.groups.length === 1).length;
